import { ipCountryQuery, configQuery, getLanguageIso6391 } from '@gig/magic-common';
import { CONFIG_QUERY, WALLET_CONFIG_QUERY, LANGUAGE_QUERY, SINGLE_LANGUAGE_QUERY, FEATURE_QUERY_ENABLED, Feature, FEATURE_QUERY_VALUE } from '@gig/magic-messages';
import { magic } from '@gig/magic-sdk';
import { ActiveBlockReasonId } from '@gig/magic-sdk/dist/adapter/users/getUserActiveBlocks';
import { WalletEnvironment, WalletView } from '@gig/magic-wallet-types';
import { Environment } from './toolkit-config.interface.js';

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class ConfigV2Service {
  constructor(config) {
    this.config = null;
    this.config = config;
    this.registerConfigQuery();
    this.registerWalletConfigQuery();
  }
  getConfig() {
    return this.config;
  }
  registerConfigQuery() {
    const handler = ({ payload: path }) => {
      if (!path) {
        return null;
      }
      return path.split(".").reduce((acc, key) => {
        if (!acc || typeof acc !== "object") {
          throw new Error(`Invalid config path = ${[path]}`);
        }
        return acc[key];
      }, Object.assign({}, this.config));
    };
    window.queryBus.subscribe(CONFIG_QUERY, handler);
  }
  registerWalletConfigQuery() {
    const handler = (_0) => __async(this, [_0], function* ({ payload }) {
      var _a, _b;
      const [configWallet, environment, user, blocks] = yield Promise.all([
        configQuery("wallet"),
        configQuery("environment"),
        magic.methods.users.getProfile(),
        magic.methods.users.getUserActiveBlocks()
      ]);
      const sessionUser = magic.session.getUser();
      const reasons = blocks.map(({ blockReasonId }) => blockReasonId);
      const isProd = environment === Environment.PRODUCTION;
      const HIGH_RISK_ROLE = "RG: High Risk";
      const language = yield window.queryBus.query(LANGUAGE_QUERY);
      const singleLanguage = yield window.queryBus.query(SINGLE_LANGUAGE_QUERY);
      const depositBonusCashierEnabled = yield window.queryBus.query(FEATURE_QUERY_ENABLED, Feature.DEPOSIT_BONUS_CASHIER);
      const origin = window.location.origin;
      const featureSetting = yield window.queryBus.query(FEATURE_QUERY_VALUE, {
        slug: Feature.MERCHANT_ID
      });
      let merchant;
      const isFeatureEnabled = featureSetting.isApplicable && featureSetting.value;
      if (isFeatureEnabled) {
        merchant = featureSetting.value;
      } else if (configWallet.merchant) {
        merchant = configWallet.merchant;
      } else {
        merchant = "";
      }
      const options = {
        cssUrl: origin + window.__magic__.files.wallet,
        path: configWallet.path,
        brandId: configWallet.brandId,
        devcodeMerchantId: merchant,
        environment: isProd ? WalletEnvironment.PRODUCTION : WalletEnvironment.DEVELOPMENT,
        languageCode: language,
        locale: `${getLanguageIso6391(language)}_${user.country}`,
        sessionId: user.igcToken,
        showErrorPages: true,
        compliance: (sessionUser == null ? void 0 : sessionUser.agreedCompliance) || false,
        allowBonusCodeOnDeposit: false,
        translationsUrl: magic.methods.translations.getTranslationsUrl(language, isProd),
        tncUrl: singleLanguage ? `${origin}/tnc` : `${origin}/${language}/tnc`,
        view: payload.view,
        disallowedWithdrawalCardTypes: yield this.getDisallowedWithdrawalCards(configWallet),
        predefinedAmounts: configWallet.predefinedAmounts,
        validateIban: configWallet.validateIban,
        fsOrgId: configWallet.fsOrgId,
        showSavedVisaWithdrawalsInfo: false,
        formatSwiftCode: false,
        googleMapsAPIKey: (_a = configWallet.googleMaps) == null ? void 0 : _a.apiKey,
        googleMapsAPIVersion: (_b = configWallet.googleMaps) == null ? void 0 : _b.apiVersion,
        paymentUrl: configWallet.paymentUrl,
        bankWithdrawalSelect: this.getBankWithdrawalSelect(configWallet),
        storedCardsLimit: yield this.getSavedDepositCardsLimit(),
        disableSwiftBankIbanWithdrawals: configWallet.disableSwiftBankIbanWithdrawals
      };
      if (reasons.includes(ActiveBlockReasonId.UserNotVerified)) {
        options.disableBonusOnDeposits = true;
      }
      if (depositBonusCashierEnabled && (sessionUser == null ? void 0 : sessionUser.roles.includes(HIGH_RISK_ROLE))) {
        options.disableBonusOnDeposits = true;
      }
      if (payload.view === WalletView.DEPOSIT) {
        options.selectedDepositMethod = payload.method;
      }
      if (payload.view === WalletView.WITHDRAWAL) {
        options.selectedWithdrawalMethod = payload.method;
      }
      return options;
    });
    window.queryBus.subscribe(WALLET_CONFIG_QUERY, handler);
  }
  getBankWithdrawalSelect(configWallet) {
    try {
      return JSON.stringify(configWallet == null ? void 0 : configWallet.bankWithdrawalSelect);
    } catch (error) {
      console.error({
        message: "Incorrect bankWithdrawalSelect value",
        error
      });
      return "";
    }
  }
  getSavedDepositCardsLimit() {
    return __async(this, null, function* () {
      const savedDepositCards = yield window.queryBus.query(FEATURE_QUERY_VALUE, {
        slug: Feature.SAVED_DEPOSIT_CARDS
      });
      let savedDepositCardsLimit;
      if (savedDepositCards.isApplicable && savedDepositCards.value) {
        savedDepositCardsLimit = Number(savedDepositCards.value);
      }
      return savedDepositCardsLimit;
    });
  }
  getDisallowedWithdrawalCards(configWallet) {
    return __async(this, null, function* () {
      const disallowed = configWallet.disallowedWithdrawalCardTypes;
      const currentIPCountry = yield ipCountryQuery();
      const disallowedCards = disallowed[currentIPCountry];
      return disallowedCards || disallowed.DEFAULT || [];
    });
  }
}

export { ConfigV2Service };
